import { useEffect, useState } from "react";
import { LOGO_URL } from "../data/content";
import { ArrowRight, Sparkles } from "lucide-react";

// Floating product orb with mouse parallax + scroll lift
const FloatingProduct = ({ src, alt, x, y, size, depth, mouse, scroll }) => {
  // depth: 1 = closest (moves most with mouse), 0.3 = far back
  const dx = (mouse.x - 0.5) * depth * 60;
  const dy = (mouse.y - 0.5) * depth * 40 + scroll * depth * -0.15;
  const rotZ = (mouse.x - 0.5) * depth * 8;
  const orbOpacity = Math.max(0, 1 - scroll / 700);
  return (
    <div
      className="absolute hidden md:block"
      style={{
        left: x,
        top: y,
        width: size,
        height: size,
        transform: `translate3d(${dx}px, ${dy}px, 0) rotate(${rotZ}deg)`,
        transition: "transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
        willChange: "transform",
        zIndex: Math.round(depth * 10),
        opacity: orbOpacity,
      }}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover rounded-full"
        style={{
          boxShadow: `0 ${20 + depth * 30}px ${40 + depth * 40}px rgba(229, 91, 91, ${0.12 + depth * 0.18}), 0 0 0 ${6 + depth * 6}px rgba(255,255,255,0.7)`,
        }}
      />
    </div>
  );
};

export const Hero = () => {
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
  const [scroll, setScroll] = useState(0);

  useEffect(() => {
    let raf = null;
    const onMouse = (e) => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setMouse({ x, y }));
    };
    const onScroll = () => setScroll(window.scrollY);
    window.addEventListener("mousemove", onMouse);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMouse);
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const heroOpacity = Math.max(0, 1 - scroll / 500);
  const heroY = scroll * 0.3;

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100vh] pt-28 pb-16 overflow-hidden"
      style={{ perspective: "1200px" }}
    >
      {/* Floating product orbs with 3D depth */}
      <FloatingProduct
        src="https://customer-assets.emergentagent.com/job_fluffy-lab/artifacts/ew6c7qzw_tira-fraise-pistache.JPG"
        alt="Tiramisu Fraise Pistache"
        x="6%" y="22%" size={140} depth={0.85}
        mouse={mouse} scroll={scroll}
      />
      <FloatingProduct
        src="https://customer-assets.emergentagent.com/job_fluffy-lab/artifacts/v4qek74i_tira-mangue-passion.JPG"
        alt="Tiramisu Mangue Passion"
        x="78%" y="18%" size={160} depth={1.0}
        mouse={mouse} scroll={scroll}
      />
      <FloatingProduct
        src="https://customer-assets.emergentagent.com/job_fluffy-lab/artifacts/t0z2322e_tira-caramel-speculos.JPG"
        alt="Tiramisu Caramel Spéculoos"
        x="12%" y="68%" size={120} depth={0.55}
        mouse={mouse} scroll={scroll}
      />
      <FloatingProduct
        src="https://customer-assets.emergentagent.com/job_fluffy-lab/artifacts/bp1x1t2r_tira-bueno.JPG"
        alt="Tiramisu Kinder Bueno"
        x="76%" y="62%" size={130} depth={0.7}
        mouse={mouse} scroll={scroll}
      />

      <div
        className="relative max-w-5xl mx-auto px-6 text-center pt-6"
        style={{
          transform: `translateY(${heroY}px)`,
          opacity: heroOpacity,
          willChange: "transform, opacity",
        }}
      >
        <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-xl pill px-4 py-2 mb-6 border border-amyu shadow-[0_8px_30px_rgba(229,91,91,0.08)]">
          <Sparkles size={14} className="text-coral" />
          <span className="text-xs uppercase tracking-[0.18em] text-amyu-ink font-medium">
            Marque gourmande fait maison
          </span>
        </div>

        <img
          src={LOGO_URL}
          alt="Amyu"
          data-testid="hero-logo"
          className="h-28 md:h-40 lg:h-48 w-auto mx-auto mb-6"
          style={{
            filter: "drop-shadow(0 24px 40px rgba(229,91,91,0.28))",
            transform: `translate3d(${(mouse.x - 0.5) * -20}px, ${(mouse.y - 0.5) * -15}px, 0)`,
            transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />

        <h1
          data-testid="hero-title"
          className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-amyu-ink tracking-tight"
        >
          Des gourmandises qui
          <br />
          <span className="italic text-coral">donnent le sourire.</span>
        </h1>

        <p
          data-testid="hero-subtitle"
          className="mt-6 max-w-2xl mx-auto text-base md:text-lg text-amyu-muted leading-relaxed"
        >
          Tiramisus artisanaux, cookies gourmands et créations exclusives — préparés maison à
          <span className="text-amyu-ink font-medium"> Carcassonne</span>, à récupérer sur notre point
          de rendez-vous.
        </p>

        <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center items-center">
          <a
            href="/commander"
            data-testid="hero-cta-order"
            className="btn-coral inline-flex items-center gap-2 text-lg px-8 py-4 shadow-[0_16px_40px_rgba(229,91,91,0.35)] hover:shadow-[0_20px_50px_rgba(229,91,91,0.45)] transition-all"
          >
            Commander <ArrowRight size={18} />
          </a>
          <a
            href="#gourmandises"
            data-testid="hero-cta-discover"
            className="inline-flex items-center gap-2 text-amyu-ink hover:text-coral transition-colors font-medium"
          >
            Découvrir nos créations
          </a>
        </div>

        <p className="mt-10 font-hand text-2xl text-coral/80">fait avec ♡ depuis 2024</p>
      </div>

      {/* Scroll hint */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-amyu-muted text-xs tracking-[0.3em] uppercase"
        style={{ opacity: Math.max(0, 1 - scroll / 200) }}
      >
        <div className="flex flex-col items-center gap-2">
          <span>Scroll</span>
          <div className="w-px h-10 bg-gradient-to-b from-coral to-transparent" />
        </div>
      </div>
    </section>
  );
};
